import { Card, Descriptions, List, Spin, Tabs, Tag, Typography } from 'antd'
import CantileverCard from 'components/CantileverCard'
import { WebGLLayer } from 'features/assetDatabase'
import { getEligibilityColor } from 'features/drilldown/panels/impl/niiTable/NiiTable'
import { useGeckoLocale } from 'features/locale'
import { useGeckoNotifications } from 'features/notifications'
import { useRolodexEntries } from 'features/rolodex'
import { useSageService } from 'features/sage'
import compact from 'lodash/compact'
import filter from 'lodash/filter'
import map from 'lodash/map'
import { FC, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  GeckoComponent,
  GeckoInspection,
  GeckoMeasurement,
  GeckoUnit,
  RolodexEntryType,
} from 'types'
import { parseString } from 'utils/parsingHelpers'

import { NiiInspectionRow } from './components/NiiInspectionRow'
import { NiiViewProps } from './NiiDetailsModal'

const { Text } = Typography

const buildNiiAnalysisQuery = (
  justification: string,
  inspectionNotes: string,
  eligibility: string
) => `
  You are an expert in non-intrusive UT inspections and their results, specialising in HOIS and API standards.

  We have attempted to determine if an asset is eligible for non-intrusive UT inspections.

  Here we have a HOIS-based assessment of whether an asset is eligible for non-intrusive UT inspections.

  Here is the assessment:
  ${justification}

  Here are the inspection notes:
  ${inspectionNotes}

  Here is the eligibility that the assessor has provided:
  ${eligibility}

  Can you please provide a one-sentence summary of the assessment that might be useful to a reviewer deciding to proceed with removing the asset from a downtime? 
  Try to keep it simple for non-native English speakers and include an answer to why the eligibility is as it is if possible.
  Can you please extract an array of key themes from the notes, justification, etc, that might be useful to a reviewer deciding to proceed with removing the asset from a downtime?
  Give a JSON response in the following format. Do not include any other text in your response and do not include any markdown code block indicators:
  {
    "summary": string,
    "themes": string[],
  }

  Your response MUST be in this format. Do not include any other text in your response:
  {
    "summary": "The asset is eligible for NII because it is a pipe and was previously approved for NII.",
    "themes": ["Pipe", "Accessible", "Low severity", "Previously assessed"],
  }
`

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const EligibilityTag = styled(Tag)<{ $eligibility: string }>`
  &.ant-tag {
    background-color: ${({ $eligibility, theme }) =>
      getEligibilityColor($eligibility, theme)};
    color: ${({ theme }) => theme.colors.grayscale['100']};
    border: none;
  }
`

const StyledCard = styled(Card)`
  background-color: ${({ theme }) => theme.colors.grayscale[600]};
  .ant-card-head {
    border-bottom: 1px solid ${({ theme }) => theme.colors.grayscale[500]};
  }
`

const StyledCantileverCard = styled(CantileverCard)`
  margin-top: 0.5rem;
`

const StyledCautionText = styled.div`
  margin-top: 0.5rem;
  font-size: 12px;
  color: red;
`

const ConclusionCautionText =
  'This NII inspection report is only valid on the next scheduled inspection assuming there are no changes to process conditions and/or expected degradation. Any changes to expected degradation will require an updated NII assessment. The results of the NII inspection should be reviewed in accordance with HOIS for conformation to expectations, and these results will feed back into the process to determine if any additional actions are required.'

export const NiiResultsView: FC<NiiViewProps> = ({
  siteEntry,
  unitEntry,
  niiData,
}) => {
  const { fetchVertexQuery } = useSageService()
  const notificationContext = useGeckoNotifications()
  const { formatMeasurement } = useGeckoLocale()
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    return () => {
      setAiAnalysis(null)
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    const fetchAiAnalysis = async () => {
      try {
        setIsLoading(true)
        const query = buildNiiAnalysisQuery(
          niiData.justification ?? '',
          niiData.inspection_notes ?? '',
          niiData.eligibility ?? ''
        )

        const aiSummaryResponse = await fetchVertexQuery({ query })
        const cleanedResponse = aiSummaryResponse.result
          .replace(/```json\n?/, '')
          .replace(/```\n?/, '')
          .trim()
        const aiSummaryParsed = JSON.parse(cleanedResponse)
        setAiAnalysis(aiSummaryParsed)
      } catch (error) {
        console.error('Error fetching AI analysis:', error)
        notificationContext.errorNotification(
          'Error fetching AI analysis. Please try again later.'
        )
      } finally {
        setIsLoading(false)
      }
    }
    fetchAiAnalysis()
  }, [niiData, fetchVertexQuery, notificationContext])

  const { entries: loadedEntries, isLoading: entriesLoading } =
    useRolodexEntries(
      useMemo(
        () =>
          unitEntry
            ? [
                {
                  type: [
                    RolodexEntryType.COMPONENT,
                    RolodexEntryType.INSPECTION,
                  ],
                  tags: { unit: unitEntry?.id },
                },
              ]
            : [],
        [unitEntry]
      )
    )

  const inspectionComponentPairList = useMemo(() => {
    const components = map(
      filter(
        loadedEntries,
        (entry) => entry.type === RolodexEntryType.COMPONENT
      ),
      (entry) => GeckoComponent.CreateFromJSON(entry)
    )
    const inspections = map(
      filter(
        loadedEntries,
        (entry) => entry.type === RolodexEntryType.INSPECTION
      ),
      (entry) => GeckoInspection.CreateFromJSON(entry)
    )

    return compact(
      map(inspections, (inspection) => {
        const component = components.find(
          (component) => component.id === inspection.tags.component
        )
        return component ? { inspection, component } : undefined
      })
    )
  }, [loadedEntries])

  const [aiAnalysis, setAiAnalysis] = useState<{
    summary: string
    themes: string[]
  } | null>(null)

  const coverageRequirementResult =
    niiData?.coverage_requirements?.map((coverage) => ({
      coverage_percentage: GeckoMeasurement.CreateFromJSON(
        coverage.coverage_percentage
      ),
      coverage_area: GeckoMeasurement.CreateFromJSON(coverage.coverage_area),
    })) || []

  return (
    <WebGLLayer>
      <ModalContent>
        <StyledCard title='Basic Information'>
          <Descriptions bordered>
            <Descriptions.Item label='Site' span={3}>
              {parseString(siteEntry?.data.name) ?? 'Unknown'}
            </Descriptions.Item>
            <Descriptions.Item label='Asset' span={3}>
              {parseString(unitEntry?.data.name) ?? 'Unknown'}
            </Descriptions.Item>
            <Descriptions.Item label='Analysis Date' span={3}>
              {niiData?.analysis_date ?? 'Unknown'}
            </Descriptions.Item>
          </Descriptions>
        </StyledCard>

        <StyledCard title='Assessment Details'>
          <Descriptions bordered>
            <Descriptions.Item label='Justification' span={3}>
              {niiData?.justification ?? 'No justification provided'}
            </Descriptions.Item>
            <Descriptions.Item label='Inspection Notes' span={3}>
              {niiData?.inspection_notes ?? 'No inspection notes provided'}
            </Descriptions.Item>
          </Descriptions>
        </StyledCard>

        <StyledCard title='NII Conclusion'>
          <Descriptions bordered>
            <Descriptions.Item label='Eligibility' span={3}>
              <EligibilityTag $eligibility={niiData?.eligibility ?? 'Unclear'}>
                {niiData?.eligibility}
              </EligibilityTag>
            </Descriptions.Item>
            <Descriptions.Item label='Recommendations' span={3}>
              <List
                size='small'
                dataSource={niiData?.recommendations}
                renderItem={(recommendation) => (
                  <List.Item style={{ padding: '0.25rem 0' }}>
                    <Text>{recommendation}</Text>
                  </List.Item>
                )}
              />
            </Descriptions.Item>
          </Descriptions>
          <StyledCautionText>* {ConclusionCautionText}</StyledCautionText>
        </StyledCard>

        <StyledCard title='Coverage Requirements'>
          <Tabs
            items={coverageRequirementResult.map((coverage, index) => ({
              key: index.toString(),
              label: `Zone ${index + 1}`,
              children: (
                <Descriptions bordered size='small' column={2}>
                  <Descriptions.Item label='Coverage Percentage' span={1}>
                    {formatMeasurement({
                      measurement: coverage.coverage_percentage,
                    })}
                  </Descriptions.Item>
                  <Descriptions.Item label='Coverage Area' span={1}>
                    {formatMeasurement({
                      measurement: coverage.coverage_area,
                    })}
                  </Descriptions.Item>
                </Descriptions>
              ),
            }))}
          />
        </StyledCard>

        <StyledCard title='Inspections'>
          <CantileverCard>
            <Spin spinning={entriesLoading} />
            {!!inspectionComponentPairList &&
              unitEntry &&
              inspectionComponentPairList.map(({ inspection, component }) => (
                <StyledCantileverCard key={inspection.id}>
                  <NiiInspectionRow
                    inspection={inspection}
                    unit={unitEntry as GeckoUnit}
                    component={component}
                    onNavigate={() => {}}
                  />
                </StyledCantileverCard>
              ))}
          </CantileverCard>
        </StyledCard>

        <StyledCard title='AI Analysis'>
          <Spin spinning={isLoading}>
            {aiAnalysis && (
              <>
                <Text strong>Summary:</Text>
                <Text style={{ display: 'block', marginBottom: '1rem' }}>
                  {aiAnalysis.summary}
                </Text>
                <Text strong>Themes:</Text>
                <div style={{ marginTop: '0.5rem' }}>
                  {aiAnalysis.themes.map((theme, index) => (
                    <Tag
                      key={index}
                      color='blue'
                      style={{ marginBottom: '0.5rem' }}>
                      {theme}
                    </Tag>
                  ))}
                </div>
              </>
            )}
          </Spin>
        </StyledCard>
      </ModalContent>
    </WebGLLayer>
  )
}
